// AccountCenter — customer account center: a registry-driven shell over the
// account sections (see account-sections.ts for the current list), absorbing and
// replacing the prior single-page AccountOverview (My Account C1, task T5 —
// design.md AC1/AC2/AC3).
//
// ⚠️ AMENDED 2026-10-03 (account-area redesign, Task 5). Orders move to the
// envelope-preserving page variant (embedApiFetchPage, limit 50, cursor
// append — TD-579) with a "Load more orders" control and partial counts; the
// order detail now opens INSIDE this shell (AccountOrderDetail, seeded from
// initialOrderId by the retired order-detail route) instead of a standalone
// full-page view, so the section nav stays visible and the active status
// segment survives the round trip; and the old `onViewOrder` prop is gone —
// navigation to orders is internal (openOrder/back), with onBrowseProducts
// as the one outbound seam (the Orders empty state routes to the catalog).
//
// ⚠️ AMENDED 2026-10-03 (account-area redesign, Task 6). The Overview
// landing is OverviewPanel.tsx (the plan's 2026-10-02 F2/F3/O1/O2 amendment:
// attention strip, gated Notifications block, Recent orders, Profile,
// Addresses, Redemption activity — the BINDING block order and the gates are
// that file's header; WelcomePanel.tsx is DELETED by this task). Cross-section
// links may now target a section TAB: navigate() takes an optional registry
// tab id, held in `pendingTab` and consumed ONCE by the destination section's
// mount (pendingTab below). Registry tab ids are the shared vocabulary; a
// panel predating the registry translates in ONE place here
// (REFER_TAB_BY_REGISTRY_ID) instead of each caller knowing both vocabularies.
// ⚠️ AMENDED 2026-10-04 (Task 10): that translation map is GONE — ReferPanel
// was rebuilt on the registry tabs and takes registry ids directly; its old
// "payouts" vocabulary is retired with the pre-rename naming.
//
// ⚠️ AMENDED 2026-10-06 (account-area redesign, Task 18; further amended the
// same day, Task 18 fix round 1 from the review gate). A store tile's
// "View all" control deep-links to the Orders section FILTERED to that store
// (ruling S4-A; the Task 6 registry-tab pattern extended with a second
// pending value): openStoreOrders(store) navigates to "orders" AND parks a
// `pendingStoreFilter`, which a consume-once effect applies — it becomes the
// APPLIED `storeFilter`, drives a filtered page-1 loadOrders, resets the
// status segment to All (a new view of that store), and clears the pending
// value. The applied filter persists across the DETAIL ROUND TRIP and is
// dropped by any navigation OUT of Orders (navigate's scope-ruling step:
// the Overview landing reads the shared list with no indicator, so a
// filter left applied would silently re-scope the whole-account view);
// the OrdersTab's explicit "Clear filter" refetches unfiltered in place.
// loadMoreOrders and a Retry both carry it so the whole surface stays on
// the one filtered list. All three loaders share one sequence token
// (loadOrdersSeqRef): a page-1 load may supersede any older call, an
// append may only ride the token it captured, so a scope change landing
// mid-flight can never repaint or extend the list with a stale scope.
// The server scopes the filter to the caller (GET /me/orders?store_id= is
// ownership-checked — a foreign store id yields the same empty page as
// "no orders", never an error).
//
// ⚠️ AMENDED 2026-10-02 (account-area redesign, Task 4). ORIGINAL: a "My Account" h1 with a
// danger "Sign Out" button and an "Acting as <Role> · <Account>" line; a nav that was a
// tablist beside the content and a <select> when narrow; seven sections with Notifications
// folded into Welcome and the stores reached from Products; and a global "Membership" card
// under every section that carried the self-leave button. CURRENT: a "My account" h1 with
// "<company> · <Role badge>" under it and a secondary "Sign out"; a <nav> of buttons
// (aria-current="page") that is a vertical list beside the content and a horizontal strip when
// narrow; nine sections (Overview, Orders, Products & subscriptions, Stores, Merchant services,
// Refer & Save, Notifications, Profile & security, Members), each opened by an EmbedPageHeader
// h2 (the focus target after a user navigation); and LeaveAccountBlock as the last child of
// Profile & security (ruling R2, F17) instead of the global card. Every section that has an ACL
// area waits for the first self-view attempt (selfViewSettled) before mounting its panel, so a
// deep link to a hidden section never fires that panel's requests. The inner headings of the
// not-yet-redesigned panels stay until their own tasks (5-11); Task 12 sweeps for leftovers.
//
// ⚠️ AMENDED 2026-09-11 (Unit-1 Task 4 — ProfilePanel extraction, review
// finding I-3). This block described a pre-Task-4 shape this diff changed.
// Preserved verbatim as ORIGINAL beside the CURRENT wording (rule 104: a
// knowingly-false header is more dangerous than none — rules 98/101 train
// readers to trust this file over derived prose).
//
// ORIGINAL (pre-Task-4, as of migration 0fc359d3):
//   "AccountCenter — customer account center: Overview / Profile / Addresses /
//    Orders / Products, absorbing and replacing the prior single-page
//    AccountOverview (My Account C1, task T5 — design.md AC1/AC2/AC3).
//
//    Tab order is an operator directive (design.md AC1): Overview · Profile ·
//    Addresses · Orders · Products — Orders BEFORE Products. Tab state lives in
//    this component (hash-routing is explicitly deferred per the plan)."
//
// CURRENT (as landed by Task 4, superseded by the Task 6 amendment directly
// below): Addresses merged (with Security) into Profile's own
// ProfilePanel.tsx; the remaining tab order was unchanged.
//
// ⚠️ AMENDED 2026-09-11 (Unit-1 Task 6 — registry-driven shell). The local
// `TabId`/`TABS` array, `EmbedTabs`, and the `effectiveTab === "…" &&`
// conditional chain are DELETED. Section id/order/label/ACL area now live in
// `./account-sections.ts` (Task 3) as `SectionId`/`SECTIONS`/
// `DEFAULT_SECTION`/`visibleSections` — this file only imports and renders
// them. That registry renamed the landing section `"overview"` → `"welcome"`
// ("Overview" → "Welcome"); the rename reaches the actual UI only now,
// because this file was still reading the old `TABS` array until this task.
// `EmbedTabs` (a 2-4-item horizontal pill) is replaced by `EmbedSectionNav`
// (Task 2): a vertical list at wide container widths, a `<select>` below
// `SECTION_NAV_BREAKPOINT_PX`. Both expose the same
// `role="tablist"`/`role="tab"`/`aria-selected` shape at wide widths, so
// existing tests' query pattern is unchanged. Section state (`activeSection`)
// still lives here; `AccountCenterProps` gains `initialSection`/
// `onNavigateSection` as the seam a future hash route would read/write — no
// URL state ships in this unit. `renderSection()`'s exhaustive `switch` over
// `SectionId` (replacing the conditional chain) is a compile error if a
// registry entry has no case — but only BECAUSE of its `: ReactElement`
// return-type annotation. This tsconfig has `strict` but NOT
// `noImplicitReturns`, so an unannotated function silently widens to
// `JSX.Element | undefined` on a missing case (JSX accepts `undefined` as a
// valid child) and typechecks clean with no error at all — verified by
// review: adding a registry entry with no case reproduced exactly that
// silent EXIT=0 before this annotation existed, and reproduced `error
// TS2366: Function lacks ending return statement…` after. Do NOT add a
// `default:` case — that would swallow the same error the annotation exists
// to raise. This is the first half of the parity guarantee between the
// registry and what renders; Task 7 adds the runtime half.
//
// Orders/Products/Merchant-Service/Refer-&-Save/Members each mount their own
// already-shipped component (OrdersTab, ProductsPanel, MidsPanel, ReferPanel,
// MembersPanel) — see each file's own header for its shipping history. This
// file stays a one-import-one-mount seam; collapsing a panel back inline
// would re-create the cross-worktree merge conflicts that design avoided.
//
// ⚠️ AMENDED 2026-09-11 (Unit-1 Task 6). ORIGINAL: TD-384 T4 named this
// section's outer tab "Processor MIDs". CURRENT: Task 3's registry
// (account-sections.ts) relabeled it "Merchant Service" (id
// "merchant-service"; ACL area ACL_AREA_MANAGE_MIDS and appended-last
// position unchanged) — the rename reaches the rendered UI only as of this
// task. MidsPanel.tsx itself is untouched and still titles its own content
// "Processor MIDs" internally; only the outer nav label/id changed.
//
// ⚠️ AMENDED 2026-09-11 (Unit-1 Task 5 — WelcomePanel extraction). ORIGINAL:
//   "and "Notifications" (NotificationsPanel, eighth — T25's first-class
//    in-app surface; a SEPARATE top-level tab, not a Refer & Save sub-tab,
//    so future non-referral kinds extend it without touching the referral
//    program's naming)."
// CURRENT: Notifications is no longer a top-level TAB — Task 5 mounts it as
// a gated sub-section of WelcomePanel.tsx (the former "overview" tab, renamed
// "welcome" as of Task 6's registry wiring), still its own component, its
// own api module (notification-api.ts) and its own ACL area
// (ACL_AREA_READ_NOTIFICATIONS), unmodified. The first-class-surface
// invariant the ORIGINAL paragraph argued for is unchanged by WHERE it is
// mounted — see WelcomePanel.tsx's own file header for why folding it into
// the account LANDING (not a feature tab) does not violate it.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens().
//
// ⚠️ AMENDED 2026-09-11 (Unit-1 Task 4, review finding I-3; further amended
// Unit-1 Task 6). ORIGINAL:
//   "composes embed-shared primitives (EmbedTabs/EmbedCard/EmbedHeading/
//    EmbedText/EmbedField/EmbedInput/EmbedButton/EmbedLink/EmbedBanner)."
// CURRENT: EmbedField/EmbedInput moved to ProfilePanel.tsx (Task 4);
// EmbedTabs replaced by EmbedSectionNav (Task 2, wired here by Task 6) — this
// file composes EmbedSectionNav/EmbedCard/EmbedHeading/EmbedText/
// EmbedButton/EmbedLink/EmbedBanner only.
//
// ACL P6 (marketplace-account-acl design §3.3): this component now reads
// GET /me/self-view on mount and uses it for PRESENTATION ONLY —
//   - an acting-as indicator (role + account) under the heading,
//   - section visibility, via the account-sections.ts registry: a LOADED
//     self-view map that says false hides a governed section. The server's
//     own area-refusal remains the control (§3.3) — hiding is UX, never
//     enforcement, so a hidden-but-requested route still 403s server-side.
//     ⚠️ AMENDED 2026-09-11 (Unit-1 Task 4, review finding I-3; terminology
//     updated Unit-1 Task 6 — "tab"→"section", "TABS array"→
//     account-sections.ts, the gating rule itself unchanged). ORIGINAL said
//     "each governed tab names its ACL area" with no exception. CURRENT:
//     "profile" is deliberately the one UNGATED section (no `area` field)
//     even though ProfilePanel's content includes two ACL-governed
//     sub-sections (manage-account-settings, manage-addresses) — gating the
//     section on either area would hide its third, always-visible
//     sub-section (Security/passkeys) for anyone denied that one area, the
//     exact narrowing this restructure forbids. See account-sections.ts's
//     own comment and ProfilePanel.tsx's file header for the full invariant.
//   - the Members section (MembersPanel) and the account-level Membership
//     card with the always-allowed self-leave affordance (§3.2 — a member
//     whose role lacks manage-membership must still be able to leave).
//
// ⚠️ AMENDED 2026-09-11 (Unit-1 Task 4, review finding I-3). This entire
// "PROFILE FIELDS" block described JSX and PATCH behavior that Task 4 moved
// to ProfilePanel.tsx. Preserved verbatim below as ORIGINAL; CURRENT: see
// ProfilePanel.tsx's own file header and its `loadAccount`/
// `handleSaveProfile` — the wire shape, nullability contract and PATCH-body
// construction described below are unchanged, just relocated to that file.
//
// ORIGINAL:
//   "PROFILE FIELDS — TD-340 (both halves) has SHIPPED.
//
//    `/me/account` projects the customer's storefront_users contact row
//    alongside the marketplace_customers row:
//      display_name  (the AC2 "contact name")  editable
//      phone                                    editable
//      email                                    DISPLAY-ONLY; PATCHing it
//                                               returns 400 EMAIL_READ_ONLY
//    See CustomerAccount in embed-shared/types.ts for the exact wire shape and
//    its nullability contract (all three keys are always present — the wire is
//    built server-side as a Go map — email is never null, the other two may
//    be). This component renders/edits display_name + phone the same way it
//    already edited company_name (empty input <-> null on save, §59 re-seed
//    from the mutation response), and renders email read-only with an
//    aria-describedby-linked note explaining it's the sign-in identity — never
//    included in the PATCH body."
import { useState, useEffect, useCallback, useRef, type ReactElement } from "react";
import {
  useEmbedTokens,
  EmbedBadge,
  EmbedButton,
  EmbedBanner,
  EmbedPageHeader,
  EmbedSectionNav,
} from "../../embed-shared/primitives";
import { embedApiFetchPage, ApiError } from "../../embed-shared/api-client";
import { useContainerWidth } from "../../embed-shared/use-container-width";
import type { OrderSummary } from "../../embed-shared/types";
import { listAddresses } from "./address-api";
import type { AddressWire } from "./address-api";
import { OrdersTab, type OrderFilterGroupId, type StoreOrdersFilter } from "./OrdersTab";
import { AccountOrderDetail } from "./AccountOrderDetail";
import { ProductsPanel } from "./ProductsPanel";
import { MidsPanel } from "./MidsPanel";
import { ReferPanel } from "./ReferPanel";
import { OverviewPanel } from "./OverviewPanel";
import { ProfilePanel } from "./ProfilePanel";
import { MembersPanel } from "./MembersPanel";
import { NotificationsPanel } from "./NotificationsPanel";
import { StoresPanel } from "./StoresPanel";
import { LeaveAccountBlock, shouldShowLeave } from "./LeaveAccountBlock";
import { type SectionId, SECTIONS, DEFAULT_SECTION, visibleSections, isAreaPermitted } from "./account-sections";
import { getSelfView, ACL_AREA_MANAGE_MIDS, type SelfViewWire } from "./membership-api";

// Flex-basis of the two shell columns below (kept as named constants so the
// breakpoint derived from them below can't silently drift from the JSX that
// actually sets `flex: "1 1 <basis>px"` on each column). Exported so the
// browser harness derives its breakpoint from them (rule 55).
// ⚠️ AMENDED 2026-10-02 (redesign Task 4, D7). ORIGINAL: nav column 180px, so the
// strip/list breakpoint was 180 + 24 + 320 = 524. CURRENT: 220px (the proposal's nav
// column), so the breakpoint is 564; D7 keeps the container-width mechanism.
export const ACCOUNT_NAV_COLUMN_BASIS_PX = 220;
export const ACCOUNT_CONTENT_COLUMN_BASIS_PX = 320;

export interface AccountCenterProps {
  onLogout?: () => void;
  /**
   * Forwarded verbatim to SecurityPanel (fix-round-2 I-1) — same runtime,
   * per-mount eligibility signal as SignInPanel's identically-named prop
   * (see that file's doc comment). Absent/false is the safe default:
   * SecurityPanel gates its "Add a Passkey" ceremony on this exactly the way
   * SignInPanel gates its passkey sign-in button, so a mount that is not the
   * aldelo.com relying party never offers a ceremony guaranteed to fail the
   * browser's own RP-ID check.
   */
  passkeyEligible?: boolean;
  /** Initial section id — the seam a future hash route would feed on first
   *  mount. Defaults to DEFAULT_SECTION ("overview"). A section the caller may
   *  not see falls back to it once the self-view settles. No URL state ships in
   *  this unit; this is only the prop-level hook for it (Unit-1 Task 6). */
  initialSection?: SectionId;
  /** Deep link: the order opened inside the Orders section on mount (the
   *  retired order-detail route feeds this — Task 5). */
  initialOrderId?: string;
  /** The Orders section's empty state routes here (the catalog section). */
  onBrowseProducts?: () => void;
  /** Fired whenever the active section changes, by click, keyboard nav, or a
   *  cross-section link (e.g. the Overview landing's "View all orders" —
   *  OverviewPanel). The seam a future hash route would write to (Unit-1
   *  Task 6). */
  onNavigateSection?: (id: SectionId) => void;
}

export function AccountCenter({
  onLogout,
  passkeyEligible,
  initialSection,
  initialOrderId,
  onBrowseProducts,
  onNavigateSection,
}: AccountCenterProps) {
  const t = useEmbedTokens();
  const [activeSection, setActiveSection] = useState<SectionId>(initialSection ?? DEFAULT_SECTION);

  // 2026-09-29 production defect: EmbedSectionNav used to self-measure its
  // OWN wrapper, which the shell below puts in a narrow (~180-200px)
  // sidebar column -- so it always read "narrow" and painted the <select>
  // on desktop/tablet too. Measure the SHELL (the two-column flex row) here
  // instead and hand EmbedSectionNav the resulting decision via `layout` --
  // see the shell's own comment below for the breakpoint's derivation.
  const [shellRef, shellWidth] = useContainerWidth();
  const accountNavShellBreakpoint =
    ACCOUNT_NAV_COLUMN_BASIS_PX + t.spacing.lg + ACCOUNT_CONTENT_COLUMN_BASIS_PX;
  // Same "unmeasured/hidden -> wide" fail-open as EmbedSectionNav's own
  // default (useContainerWidth's doc comment) and the same 0-width guard
  // (a display:none shell's first contentRect observation is 0x0, which
  // would otherwise misread as narrower-than-breakpoint).
  const accountNavLayout: "list" | "strip" =
    shellWidth !== null && shellWidth > 0 && shellWidth < accountNavShellBreakpoint
      ? "strip"
      : "list";

  // ---- ACL P6: self-view (§3.3) + self-leave (§3.2). ----
  const [selfView, setSelfView] = useState<SelfViewWire | null>(null);
  // Unit-1 Task 5 review fix: distinguishes "no attempt has resolved yet"
  // from "attempted and resolved to null" — `isAreaPermitted(null, area)` is
  // deliberately fail-open (§3.3), so without this flag a gated surface
  // (⚠️ AMENDED 2026-10-03, redesign Task 6: the consumer was WelcomePanel's
  // Notifications sub-section; it is now OverviewPanel's gated Notifications
  // block plus every ACL-gated SECTION — React flushes passive effects
  // child-first) mounts and fires its fetch before THIS component's own
  // loadSelfView() effect has even started, flashing a forbidden surface at
  // a denied member. MUST start `false` and be set `true` only in loadSelfView's
  // `finally` below (both outcomes) and NEVER reset afterward — later
  // re-fetches (leave, post-transfer, MembersPanel's onSelfViewStale) must
  // not re-hide the panel while refreshing. Not a security hole either way
  // (the route is ACL-gated server-side); this only removes an avoidable
  // request + UI flash.
  const [selfViewSettled, setSelfViewSettled] = useState(false);
  // The page-level "You have left <account>." banner. It lives here, not in LeaveAccountBlock,
  // because the block unmounts as soon as the reloaded self-view has no membership row.
  const [leaveDone, setLeaveDone] = useState<string | null>(null);
  // Task-4 gate advisory (folded into Task 8; AMENDED 2026-10-04, review
  // P2 fix round 3). ORIGINAL rationale: the banner's wrapper was ALWAYS
  // mounted so the focus target would "pre-exist the announcement". That
  // cost a real layout regression: the wrapper sat as an EMPTY flex item
  // in the root's 24px-gap column, adding a second header→shell gap to
  // every account page. The FOCUS contract never needed the pre-existence:
  // the wrapper now mounts in the SAME commit as the banner text (the
  // effect below runs after that commit, so the ref is attached), which is
  // exactly the insertion-+-focus pattern SecurityPanel's removal
  // announcement already uses. Before the ORIGINAL fix the success path
  // rendered the banner, moved no focus, and the browser dropped focus to
  // <body> -- that part of the rationale stands.
  const leaveDoneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (leaveDone) {
      leaveDoneRef.current?.focus();
    }
  }, [leaveDone]);

  const loadSelfView = useCallback(async () => {
    try {
      setSelfView(await getSelfView());
    } catch {
      // Presentation-only degradation (§3.3): a failed self-view read keeps
      // pre-P6 section visibility — the server's own area refusals remain
      // the control, so the worst case is a visible section whose use 403s,
      // exactly the pre-ACL behavior. Deliberately no error banner: this
      // read backs chrome, not a section the customer asked for.
      setSelfView(null);
    } finally {
      // Both outcomes count as "settled" — a failed read must NOT gate the
      // Notifications mount closed (fail-open stays fail-open; see the
      // `selfViewSettled` declaration above).
      setSelfViewSettled(true);
    }
  }, []);

  useEffect(() => {
    loadSelfView();
  }, [loadSelfView]);

  // Presentation gating (§3.3), via the registry: a section hides only on a
  // LOADED self-view map's explicit `false`; unloaded/failed stays fail-open
  // (blanking the page on a transient fetch failure would be worse). Derived
  // in render, not an effect, so a mid-session denial (e.g. post-transfer
  // demotion) re-derives immediately.
  const visible = visibleSections(selfView);
  // A section that just became hidden must not keep rendering — fall back
  // to welcome (e.g. the old owner sitting on Members when a transfer
  // demotes them to manager, whose default cell denies it).
  const effectiveSection: SectionId =
    visible.some((s) => s.id === activeSection) ? activeSection : DEFAULT_SECTION;

  // A user navigation moves focus to the new section's heading; the initial mount never does
  // (the host page keeps focus). The nonce is bumped only by navigate(); the effect itself
  // lives just below `awaitingSelfView`, which is one of its deps (see its own comment).
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [focusNonce, setFocusNonce] = useState(0);
  // Redesign Task 6: a cross-section link can target a section TAB ("View
  // redemptions" lands on Refer & Save's Redemptions tab). The pending
  // registry tab id rides the SAME navigate() call as the section; the
  // destination panel receives it as `initialSubTab` (a REGISTRY id — since
  // Task 10 ReferPanel validates it against SECTION_TABS.refer itself) and
  // consumes it in its mount-time state initializer, so this holds it only
  // until the section's first render.
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  // Task 18's second pending value: a store tile's "View all" parks the
  // target store here; the consume-once effect below turns it into the
  // APPLIED `storeFilter` exactly once (later returns to Orders must keep
  // whatever filter state they have, not re-apply a stale link).
  const [pendingStoreFilter, setPendingStoreFilter] = useState<StoreOrdersFilter | null>(null);
  // navigate/openOrder/openStoreOrders live BELOW the orders state + loadOrders
  // (Task 18 fix round 1): navigate's drop-the-filter-on-leaving-Orders step
  // reads both, and a deps array is evaluated at declaration time — declared
  // above them it would be a TDZ ReferenceError, not a hoisting question.

  // ---- Orders + addresses: fetched on mount (⚠️ AMENDED 2026-10-03,
  // redesign Task 6: the consumer was WelcomePanel's summary, deleted with
  // that panel — the Overview blocks need both now: Recent orders and
  // Addresses; the same lists still back the Orders section and the Profile
  // section's Addresses sub-section). ----
  // ⚠️ AMENDED 2026-10-03 (redesign Task 5, TD-579). ORIGINAL: one
  // unpaginated `embedApiFetch` GET of /me/orders ("per-customer volume is
  // small — no pagination"). CURRENT: the envelope-preserving page variant,
  // 50 rows per page (the wire's page size), appending each page's
  // `next_cursor` batch — the order list is the one surface a long-lived
  // customer can actually grow past one page. Overview keeps reading the same
  // accumulated list (its Recent orders block wants the newest orders, which
  // page 1 holds).
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  // The pagination cursor state (the plan's `ordersPage`): has_next drives
  // the "Load more orders" control; next_cursor is appended to the next call.
  const [ordersHasNext, setOrdersHasNext] = useState(false);
  const [ordersNextCursor, setOrdersNextCursor] = useState<string | null>(null);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);
  // Review P2 (append contract): an append failure is NOT a list failure.
  // Reusing ordersError here would hide the loaded pages (OrdersTab gates the
  // list on !error) and paint the Overview landing as failed too
  // (OverviewPanel renders ordersError as its alert). This state never gates
  // the list — OrdersTab surfaces it beside the Load-more control, which
  // stays clickable as the retry.
  const [ordersAppendError, setOrdersAppendError] = useState<string | null>(null);

  // The order opened inside the shell (Task 5): non-null ⇒ the Orders section
  // renders AccountOrderDetail instead of the list. initialOrderId seeds it so
  // the retired order-detail route's deep link lands on the same view.
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(initialOrderId ?? null);
  // The Orders section's segment is lifted HERE (not local to OrdersTab) so
  // it survives the detail round trip: open an order, come back, still be on
  // "Shipped".
  const [ordersSegment, setOrdersSegment] = useState<OrderFilterGroupId>("all");
  // Task 18: the APPLIED per-store filter — null = the unfiltered list.
  // ⚠️ AMENDED 2026-10-06 (Task 18 fix round 1, the Overview scope ruling).
  // ORIGINAL: "Persists like ordersSegment (across the detail round trip and
  // section switches)". CURRENT: it persists across the DETAIL ROUND TRIP and
  // is dropped by any navigation OUT of Orders (navigate's ruling step
  // below) — the shared orders array backs the Overview landing, whose
  // blocks render no scope indicator, so an outlived filter would re-scope
  // the whole-account view silently. The deep link from a store tile arrives
  // as pendingStoreFilter and is applied exactly once by the effect below.
  const [storeFilter, setStoreFilter] = useState<StoreOrdersFilter | null>(null);

  const [addresses, setAddresses] = useState<AddressWire[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressesError, setAddressesError] = useState<string | null>(null);

  const ORDERS_PAGE_SIZE = "50";

  // `filter` is an ARGUMENT, not a dependency (Task 18): the mount-once
  // effect calls loadOrders() unfiltered without re-triggering when the
  // applied store filter later changes — a filtered load is started
  // explicitly (the deep-link apply effect / the Clear handler / Retry).
  // In-flight guard (review P2, Task 18): loadOrders callers can now overlap
  // (the mount fetch vs the deep-link/Clear refetch), and without a guard the
  // last response to LAND wins — a stale unfiltered response arriving after a
  // filtered one repaints the full list under the "Showing orders for <store>"
  // indicator and strands an unfiltered cursor that loadMoreOrders appends a
  // filtered page onto. Each call takes a sequence token; a superseded
  // completion applies NONE of its state (loadingOrders included — the newer
  // call owns that flag until it settles).
  const loadOrdersSeqRef = useRef(0);
  const loadOrders = useCallback(async (filter?: StoreOrdersFilter | null) => {
    const seq = ++loadOrdersSeqRef.current;
    setLoadingOrders(true);
    setOrdersError(null);
    try {
      // Page 1 always restarts the list (a retry must not silently keep a
      // half-loaded cursor chain). Go handler: HandleOrdersListPage emits
      // {data, pagination:{next_cursor, has_next}}; embedApiFetchPage keeps
      // the envelope (§39 verified). The store filter (Task 18) rides the
      // same query — the server scopes it to the caller, and its offset
      // pagination composes with the filter unchanged.
      const page = await embedApiFetchPage<OrderSummary[]>("/api/v1/marketplace/me/orders", {
        query: { limit: ORDERS_PAGE_SIZE, ...(filter ? { store_id: filter.id } : {}) },
      });
      if (seq !== loadOrdersSeqRef.current) return;
      setOrders(page.data ?? []);
      setOrdersHasNext(page.pagination?.has_next ?? false);
      setOrdersNextCursor(page.pagination?.next_cursor ?? null);
    } catch (err) {
      if (seq !== loadOrdersSeqRef.current) return;
      setOrdersError(err instanceof ApiError ? err.message : "Failed to load orders");
    } finally {
      if (seq === loadOrdersSeqRef.current) setLoadingOrders(false);
    }
  }, []);

  // An explicit section navigation always lands on the section itself — for
  // Orders that means the LIST, so a detail left open by openOrder must not
  // shadow it ("View all orders" and the Orders nav click both arrive here).
  // openOrder sets the id AFTER calling navigate, so the batched updates
  // still land on the detail.
  //
  // Task 18 fix round 1 (the review gate's Overview scope ruling): the
  // shared `orders` array also backs the Overview landing's Recent orders
  // block, which renders NO scope indicator — a filter left applied across a
  // section switch silently re-scopes the whole-account Overview, and a
  // return to Orders resumes a filter whose indicator the customer never saw
  // appear. Navigating AWAY from Orders therefore DROPS the filter and
  // refetches unfiltered; navigating WITHIN Orders (openOrder's detail view,
  // openStoreOrders' deep link) keeps it — the detail round trip and the
  // deep link are exactly the contracts the filter persists across. The
  // refetch rides the sequence token, so any in-flight filtered page-1 is
  // invalidated by it. (Declared below `storeFilter`/`loadOrders`: the deps
  // array is evaluated at declaration time.)
  const navigate = useCallback(
    (id: SectionId, tab?: string) => {
      setActiveSection(id);
      setPendingTab(tab ?? null);
      setViewingOrderId(null);
      if (storeFilter !== null && id !== "orders") {
        setStoreFilter(null);
        void loadOrders(null);
      }
      setFocusNonce((n) => n + 1);
      onNavigateSection?.(id);
    },
    [onNavigateSection, storeFilter, loadOrders],
  );

  // Opens an order inside the shell (Task 5): the content pane swaps to
  // AccountOrderDetail until the detail's back link clears it. The click can
  // come from anywhere an order is surfaced — the Overview's recent-orders
  // rows included — so it also lands the user on the Orders section: the
  // nav's aria-current follows the visible content, and "Back to orders"
  // restores the Orders LIST, not whatever section the click came from.
  const openOrder = useCallback(
    (orderId: string) => {
      navigate("orders");
      setViewingOrderId(orderId);
    },
    [navigate],
  );

  // Task 18: a store tile's "View all" — land on Orders filtered to that
  // store. navigate() first (clears any open detail), THEN parks the pending
  // filter for the consume-once effect below; only {id, name} is needed, so
  // the parameter is structural (CustomerStoreWire satisfies it without this
  // shell importing the wire type).
  const openStoreOrders = useCallback(
    (store: { id: string; name: string }) => {
      navigate("orders");
      setPendingStoreFilter({ id: store.id, name: store.name });
    },
    [navigate],
  );

  // In-flight guard, append half (Task 18 fix round 1): the append CAPTURES
  // the token of the load whose cursor it is about to consume and applies
  // NOTHING when a newer loadOrders has since restarted the list — an apply
  // or Clear landing mid-append would otherwise append a stale-scope page
  // onto the restarted list AND leave the OLD scope's cursor driving it. The
  // append deliberately does NOT bump the token: page 1 defines the scope, so
  // every loadOrders outranks every append (and while a restart is in flight
  // the list is gated out of the DOM, so the control is not even clickable).
  // A captured-filter comparison — the alternative considered in review — is
  // subsumed by the token: every storeFilter transition in this component is
  // paired with a loadOrders call, which is what moves the token.
  const loadMoreOrders = useCallback(async () => {
    if (!ordersNextCursor || loadingMoreOrders) return;
    const seqAtStart = loadOrdersSeqRef.current;
    setLoadingMoreOrders(true);
    setOrdersAppendError(null);
    try {
      const page = await embedApiFetchPage<OrderSummary[]>("/api/v1/marketplace/me/orders", {
        // Task 18: the applied store filter carries into appends — the
        // cursor pages through the FILTERED list, never back to all orders.
        query: {
          limit: ORDERS_PAGE_SIZE,
          cursor: ordersNextCursor,
          ...(storeFilter ? { store_id: storeFilter.id } : {}),
        },
      });
      if (seqAtStart !== loadOrdersSeqRef.current) return;
      // Appended, never replaced — the customer's scroll position is the
      // whole point of the control.
      //
      // Deduped by id at this boundary: the cursor is a pure OFFSET
      // (customer_handler.go encodeOffsetCursor base64s `offset + limit`) over
      // `ORDER BY created_at DESC, id ASC LIMIT/OFFSET` (order_pgx.go), so an
      // order the customer places between page 1 and Load-more shifts every
      // row down and this page's OFFSET re-serves a row already on screen.
      // Keep the ALREADY-LOADED copy: newest-first means the re-served row is
      // the same row read twice, and the earlier position is the one the
      // customer saw (a blind append rendered it twice and drifted the
      // segment counts).
      setOrders((cur) => {
        const seen = new Set(cur.map((o) => o.id));
        return [...cur, ...(page.data ?? []).filter((o) => !seen.has(o.id))];
      });
      setOrdersHasNext(page.pagination?.has_next ?? false);
      setOrdersNextCursor(page.pagination?.next_cursor ?? null);
    } catch (err) {
      if (seqAtStart !== loadOrdersSeqRef.current) return;
      setOrdersAppendError(err instanceof ApiError ? err.message : "Failed to load more orders");
    } finally {
      // loadingMoreOrders is the append's OWN flag, not list-scope state — no
      // other append can be pending (the top guard is mutual exclusion), so
      // it resets unconditionally; a superseded append must not wedge the
      // restarted list's control off.
      setLoadingMoreOrders(false);
    }
  }, [ordersNextCursor, loadingMoreOrders, storeFilter]);

  const loadAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    setAddressesError(null);
    try {
      const data = await listAddresses();
      setAddresses(data);
    } catch (err) {
      setAddressesError(err instanceof ApiError ? err.message : "Failed to load addresses");
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  useEffect(() => {
    // Mount-once (loadOrders/loadAddresses are useCallback-stable with no
    // deps, so listing them here doesn't cause extra runs): both lists back
    // the Overview blocks (Recent orders / Addresses — Task 6; the consumer
    // was the deleted WelcomePanel's summary) regardless of which section
    // the customer lands on first.
    loadOrders();
    loadAddresses();
  }, [loadOrders, loadAddresses]);

  // Profile/Addresses-detail/Security state (account form, addresses
  // interaction state) lives in ProfilePanel — Task 4, Unit-1 IA
  // restructure. AccountCenter keeps only the address LIST + its fetch
  // above (shared data Welcome counts from).

  // Acting-as label: role capitalized verbatim (the embed convention since
  // the retired MidsPanel StatusPill — capitalize in JS, not CSS, because
  // textTransform is cosmetic-only and the DOM text is what tests and
  // copy-paste see). Rendered only when self-view actually resolved a role;
  // the "" legacy state renders nothing (the UI mirrors the same
  // nothing-allowed state the server enforces).
  const roleLabel = selfView?.role
    ? selfView.role.charAt(0).toUpperCase() + selfView.role.slice(1)
    : null;
  const accountLabel = selfView?.account
    ? (selfView.account.company_name ?? `Account #${selfView.account.id}`)
    : null;

  // Exhaustive over SectionId — a registry entry with no case is a COMPILE
  // error ONLY because of the `: ReactElement` return type below (this
  // tsconfig lacks noImplicitReturns; see the Task-6 header amendment for
  // why an unannotated function would silently accept a missing case).
  // Task 7 adds the runtime half.
  function renderSection(): ReactElement {
    switch (effectiveSection) {
      case "overview":
        // Redesign Task 6 (the F2 amendment): the landing is OverviewPanel —
        // WelcomePanel is DELETED by this task. The amendment's binding block
        // order, gates and settle discipline are OverviewPanel.tsx's header.
        // selfView/selfViewSettled are the same presentation gate the shell
        // already owns; the panel dispatches nothing pre-settle (F3).
        return (
          <OverviewPanel
            selfView={selfView}
            selfViewSettled={selfViewSettled}
            orders={orders}
            loadingOrders={loadingOrders}
            ordersError={ordersError}
            addresses={addresses}
            loadingAddresses={loadingAddresses}
            onOpenOrder={openOrder}
            onNavigateSection={navigate}
            onBrowseProducts={onBrowseProducts}
          />
        );
      case "profile":
        return (
          // Task 8 amendment (F17/F21): the Leave block travels as the
          // ProfilePanel leaveSlot -- the PANEL owns the placement (Profile
          // tab, always that pane's LAST element child), this shell keeps
          // owning the shouldShowLeave condition and the callbacks. The
          // conditional lives in the PROP, not around it: leaveSlot={null}
          // for an owner renders nothing, identical to the PRE fragment's
          // second child being absent.
          <ProfilePanel
            selfView={selfView}
            selfViewSettled={selfViewSettled}
            addresses={addresses}
            addressesError={addressesError}
            loadingAddresses={loadingAddresses}
            onAddressesChanged={setAddresses}
            passkeyEligible={passkeyEligible}
            initialSubTab={pendingTab ?? undefined}
            leaveSlot={
              shouldShowLeave(selfView) ? (
                <LeaveAccountBlock accountLabel={accountLabel} onLeft={setLeaveDone} reloadSelfView={loadSelfView} />
              ) : null
            }
          />
        );
      case "orders":
        // Task 5: the order opens INSIDE the shell — AccountOrderDetail below
        // the section nav (the nav stays visible); back returns to the list
        // with the segment state preserved (it lives in this component).
        // Task 18: Retry and the store filter stay on the applied filter —
        // onRetry is a CLOSURE (not the bare loadOrders) so a retry of a
        // filtered list refetches that same filter, not all orders.
        if (viewingOrderId) {
          return <AccountOrderDetail orderId={viewingOrderId} onBack={() => setViewingOrderId(null)} />;
        }
        return (
          <OrdersTab
            orders={orders}
            loading={loadingOrders}
            error={ordersError}
            onRetry={() => loadOrders(storeFilter)}
            onOpenOrder={openOrder}
            hasMore={ordersHasNext}
            loadingMore={loadingMoreOrders}
            onLoadMore={loadMoreOrders}
            appendError={ordersAppendError}
            onBrowseProducts={onBrowseProducts}
            segmentId={ordersSegment}
            onSegmentChange={setOrdersSegment}
            storeFilter={storeFilter}
            onClearStoreFilter={clearStoreFilter}
          />
        );
      case "products":
        // TD-583: each Products panel degrades on ITS OWN data area, so the panel needs the
        // same self-view the shell gates on (an unloaded one permits everything).
        return <ProductsPanel selfView={selfView} />;
      case "stores":
        // Task 18: tiles surface orders — a compact card opens the order in
        // the shell; "View all" deep-links to the filtered Orders section.
        return <StoresPanel selfView={selfView} onOpenOrder={openOrder} onViewStoreOrders={openStoreOrders} />;
      case "merchant-service":
        return <MidsPanel />;
      case "refer": {
        // Task 10: the pending tab IS a registry id — ReferPanel validates it
        // against SECTION_TABS.refer at mount (an unknown or retired id falls
        // back to Welcome; later renders with initialSubTab undefined cannot
        // re-point it, which is the point). onNavigateSection wires the redeem
        // dialog's "Link a merchant account in Merchant services" escape
        // hatch to the shell's navigate.
        return (
          <ReferPanel
            initialSubTab={pendingTab ?? undefined}
            onNavigateSection={navigate}
            // TD-605: manage-mids decides whether the redeem dialog may read the
            // account list; without it the dialog redeems by store.
            canViewAccounts={isAreaPermitted(selfView, ACL_AREA_MANAGE_MIDS)}
          />
        );
      }
      case "notifications":
        // The panel owns its fetch, so it mounts only once the first self-view attempt has
        // settled (see the settled gate in the render below). Account-area redesign Task 6
        // restyled it (one card of shared NotificationListItem rows).
        return (
          <section aria-label="Notifications">
            <NotificationsPanel />
          </section>
        );
      case "members":
        return <MembersPanel selfView={selfView} onSelfViewStale={loadSelfView} />;
    }
  }

  const current = SECTIONS.find((sec) => sec.id === effectiveSection) ?? SECTIONS[0];
  // Gated sections wait for the first self-view attempt before their panel mounts: an unloaded
  // self-view fails OPEN (isAreaPermitted), so mounting earlier would let a denied member's
  // browser fire that panel's requests (and flash its title) before the ACL answer arrives.
  // Failure still counts as settled, so a failed read never blocks a section.
  const awaitingSelfView = current.area !== undefined && !selfViewSettled;

  // Consume-once (Task 6): the FIRST render of the destination section takes
  // the pending tab; later returns to that section (nav clicks, the detail
  // round trip) must keep their own state instead of re-applying a stale
  // link target. Gated sections render a placeholder until the self-view
  // settles, so the consume also waits for `awaitingSelfView` to drop —
  // clearing during the placeholder would lose the tab before the panel ever
  // mounted. (This lives BELOW awaitingSelfView's declaration: the dep array
  // is read during render, and a use-before-declare const is a TDZ error.)
  useEffect(() => {
    if (!awaitingSelfView && pendingTab !== null) setPendingTab(null);
  }, [awaitingSelfView, pendingTab]);

  // Consume-once, Task 18's store-filter twin: the deep link from a store
  // tile's "View all" is applied exactly ONCE — it becomes the applied
  // storeFilter, restarts the list with that filter, resets the status
  // segment to All (a new view of that store: a segment a previous list left
  // selected could combine with the filter into a confusing near-empty
  // view), and clears the pending value so later returns to Orders keep
  // their own state. Unlike pendingTab this does NOT wait for
  // awaitingSelfView: Orders is ungated pending-side — the effect only
  // fires a fetch, and the Orders section's render gate is the section
  // switch itself.
  useEffect(() => {
    if (pendingStoreFilter === null) return;
    setStoreFilter(pendingStoreFilter);
    setOrdersSegment("all");
    void loadOrders(pendingStoreFilter);
    setPendingStoreFilter(null);
  }, [pendingStoreFilter, loadOrders]);

  // Task 18: the OrdersTab indicator's Clear control — drop the applied
  // filter and refetch the unfiltered list. The segment is deliberately NOT
  // reset here: clearing returns to the full list, whose segment state the
  // customer may have been navigating before the detour.
  const clearStoreFilter = useCallback(() => {
    setStoreFilter(null);
    void loadOrders(null);
  }, [loadOrders]);

  // Runs after the new section has rendered. `awaitingSelfView` is a dep, not just the nonce:
  // a navigation issued before the first self-view settles renders the Loading... placeholder
  // (no [data-section-heading] yet), so the nonce-triggered run no-ops on null; when the settle
  // swaps the placeholder for the header the nonce alone doesn't change, and without this dep
  // focus would never move. It flips at most once per mount (selfViewSettled never resets), so
  // this never re-steals focus on a settled section; focusNonce===0 still guards the initial
  // mount (the host page keeps focus).
  useEffect(() => {
    if (focusNonce === 0) return;
    contentRef.current?.querySelector<HTMLElement>("[data-section-heading]")?.focus();
  }, [focusNonce, awaitingSelfView]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.lg }}>
      <EmbedPageHeader
        level={1}
        title="My account"
        meta={
          accountLabel ? (
            <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: t.spacing.sm, minWidth: 0 }}>
              <span style={{ overflowWrap: "anywhere", minWidth: 0 }}>{accountLabel}</span>
              {roleLabel && <span aria-hidden="true">·</span>}
              {roleLabel && <EmbedBadge tone="neutral">{roleLabel}</EmbedBadge>}
            </span>
          ) : null
        }
        action={
          onLogout ? (
            <EmbedButton type="button" variant="secondary" size="sm" onClick={onLogout}>Sign out</EmbedButton>
          ) : null
        }
      />

      {/* Task-4 gate advisory (folded into Task 8; fix round 3, P2): the
          status wrapper renders ONLY while there is something to announce.
          An always-mounted version was a zero-height flex item in this
          24px-gap column -- a permanent second header→shell gap on every
          account page. Mounting here, in the same commit as the text, keeps
          the focus effect working (see the amended comment at
          leaveDoneRef). */}
      {leaveDone && (
        <div ref={leaveDoneRef} data-testid="leave-done-status" role="status" tabIndex={-1}>
          <EmbedBanner tone="success">{leaveDone}</EmbedBanner>
        </div>
      )}

      {/* Two-column shell: nav beside content at wide widths, a horizontal strip above the
          content below accountNavShellBreakpoint (driven by the `layout` prop, measured on THIS
          row rather than on the nav's own narrow column).
          minWidth: 0 on BOTH columns is LOAD-BEARING, not styling — without
          it a flex item refuses to shrink below its content's intrinsic
          width and the account area scrolls horizontally at 400px, the
          defect this unit fixes.
          ⚠️ AMENDED 2026-09-29 (embed account nav width fix). ORIGINAL: the swap happened
          "below SECTION_NAV_BREAKPOINT_PX", implying EmbedSectionNav's own self-measurement
          was the intended signal. CURRENT: that was the production defect — it measured the NAV
          COLUMN's own width (~180-200px, always < 720), not this shell's. This div is what gets
          measured (shellRef); the two columns wrap onto separate lines exactly when its
          content-box width drops below ACCOUNT_NAV_COLUMN_BASIS_PX + gap +
          ACCOUNT_CONTENT_COLUMN_BASIS_PX (flex line-breaking runs on each item's flex-basis, not
          its post-shrink size — minWidth:0 only lets the columns shrink AFTER they are placed on
          a line). Below that width the nav wraps to sit full-width ABOVE the content.
          ⚠️ AMENDED 2026-10-02 (redesign Task 4, D7). ORIGINAL: the narrow control was a
          <select> and the breakpoint 524 (180 + 24 + 320). CURRENT: a horizontal strip, and the
          breakpoint 564 because the nav column is 220px. */}
      <div ref={shellRef} data-testid="account-nav-shell" style={{ display: "flex", flexWrap: "wrap", gap: t.spacing.lg, alignItems: "flex-start" }}>
        <div
          data-testid="account-nav-column"
          style={{
            flex: `1 1 ${ACCOUNT_NAV_COLUMN_BASIS_PX}px`,
            maxWidth: accountNavLayout === "list" ? ACCOUNT_NAV_COLUMN_BASIS_PX : "100%",
            minWidth: 0,
            position: accountNavLayout === "list" ? "sticky" : "static",
            top: t.spacing.md,
          }}
        >
          <EmbedSectionNav
            aria-label="Account sections"
            items={visible.map((s) => ({ id: s.id, label: s.label }))}
            value={effectiveSection}
            onChange={(id) => navigate(id as SectionId)}
            layout={accountNavLayout}
          />
        </div>
        <div
          ref={contentRef}
          data-testid="account-section-content"
          style={{ flex: `999 1 ${ACCOUNT_CONTENT_COLUMN_BASIS_PX}px`, minWidth: 0, maxWidth: 960 }}
        >
          {awaitingSelfView ? (
            <p role="status" style={{ margin: 0, color: t.colors.textSecondary, fontSize: t.fontSize.sm }}>Loading...</p>
          ) : (
            <>
              <EmbedPageHeader title={current.label} description={current.description} focusTarget />
              {renderSection()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
